import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { DailyIcon } from "@/features/daily/components/DailyIcon";
import { dailyFormat } from "@/features/daily/components/command/command-view-model";
import { floatingPanel, type FloatingPanelPosition } from "@/features/daily/components/command/floating-panel";
import {
    commandCenterErrorsModel,
    type CommandCenterErrorsBar,
    type CommandCenterErrorsWindow,
} from "@/features/observability/components/CommandCenterErrorsModel";
import { frontendErrorsFormat } from "@/features/observability/components/frontend-errors-format";
import type { FrontendErrorsResource, FrontendErrorsStatus } from "@/features/observability/hooks/use-frontend-errors";
import type { FrontendErrorsGroups, FrontendErrorsSummary } from "@/features/observability/types/frontend-errors.types";
import "@/features/observability/components/CommandCenterErrors.css";

const LOSS_TIP = "Puede quedarse corta (cobertura nunca demostrada) o pasarse (con varias pestañas el exceso se suma por pestaña). No es un mínimo ni un máximo.";

type FailureStatus = Extract<FrontendErrorsStatus, "unavailable" | "forbidden" | "error">;

const FAILURE_TEXT: Record<FailureStatus, string> = {
    forbidden: "Sin acceso al lector de errores",
    unavailable: "Fuente no disponible",
    error: "Fuente no disponible",
};

type CardState<T> = { kind: "loading" } | { kind: "failed"; status: FailureStatus } | { kind: "ready"; data: T; lowerBound: boolean };

// Only ok/degraded carry data; every other status is rendered as text and never dereferences it.
const cardState = <T extends { countsAreLowerBound: boolean }>(resource: FrontendErrorsResource<T>): CardState<T> => {
    const status = resource.status;
    if (status === "unavailable" || status === "forbidden" || status === "error") return { kind: "failed", status };
    if ((status === "ok" || status === "degraded") && resource.data !== null) {
        return { kind: "ready", data: resource.data, lowerBound: status === "degraded" || resource.data.countsAreLowerBound };
    }
    return { kind: "loading" };
};

const groupsCardState = (
    summary: FrontendErrorsResource<FrontendErrorsSummary>,
    groups: FrontendErrorsResource<FrontendErrorsGroups>,
): CardState<FrontendErrorsGroups> => {
    const first = cardState(summary);
    if (first.kind !== "ready") return first;
    return cardState(groups);
};

const withBound = (value: number | null, lowerBound: boolean): string => {
    const text = dailyFormat.count(value);
    return value !== null && lowerBound ? `${text} mín.` : text;
};

const CardBody: React.FC<{ state: CardState<unknown>; label: string; children: React.ReactNode }> = ({ state, label, children }) => {
    if (state.kind === "loading") {
        return (
            <div className="cc-errors__skeleton" aria-busy="true">
                <span className="daily-visually-hidden">{`Cargando ${label}`}</span>
            </div>
        );
    }
    if (state.kind === "failed") return <p className="cc-errors__notice">{FAILURE_TEXT[state.status]}</p>;
    return <>{children}</>;
};

const PageLink: React.FC<{ range: CommandCenterErrorsWindow }> = ({ range }) => (
    <Link className="daily-link" to={commandCenterErrorsModel.href(range, null)}>
        Ver errores del frontend <DailyIcon name="arrow_forward" />
    </Link>
);

type CardProps = {
    range: CommandCenterErrorsWindow;
    summary: FrontendErrorsResource<FrontendErrorsSummary>;
};

const DETAIL_WIDTH = 300;
const DETAIL_HEIGHT = 420;
const DETAIL_ID = "cc-errors-bar-detail";
const oneDecimal = new Intl.NumberFormat("es-ES", { minimumFractionDigits: 1, maximumFractionDigits: 1 });

// Design v2: «40,0 %» — one decimal and a non-breaking space before the sign.
const shareText = (share: number | null): string => (share === null ? dailyFormat.absent : `${oneDecimal.format(share * 100)}\u00a0%`);

type DetailProps = { bar: CommandCenterErrorsBar; lowerBound: boolean; asOf: number; position: FloatingPanelPosition };

const DetailStat: React.FC<{ label: string; badge: string | null; value: string; hint: string }> = ({ label, badge, value, hint }) => (
    <div className="cc-errors__detail-stat">
        <span className="cc-errors__detail-label">
            {label}
            {badge === null ? null : <span className="cc-errors__badge">{badge}</span>}
        </span>
        <span className="cc-errors__detail-value">{value}</span>
        <span className="cc-errors__detail-hint">{hint}</span>
    </div>
);

const BarDetail: React.FC<DetailProps> = ({ bar, lowerBound, asOf, position }) => {
    const detail = bar.detail;
    const classes = detail.errorClasses.length === 0 ? dailyFormat.absent : detail.errorClasses.join(", ");
    const statuses = detail.httpStatuses.length === 0 ? "sin HTTP" : `HTTP ${detail.httpStatuses.join(", ")}`;
    const share = `${shareText(detail.share)} del total${lowerBound ? " · aproximado" : ""}`;
    const perDevice = detail.perDevice === null ? dailyFormat.absent : oneDecimal.format(detail.perDevice);
    return (
        <div id={DETAIL_ID} role="tooltip" className="cc-errors__detail" data-category={bar.category} style={{ left: position.left, top: position.top, width: DETAIL_WIDTH }}>
            <div className="cc-errors__detail-head">
                <span className="cc-errors__chip">
                    <span className="cc-errors__swatch" aria-hidden="true" />
                    {frontendErrorsFormat.categoryStyle(bar.category).label}
                </span>
                <span className="cc-errors__detail-hint">Últimas 24 h</span>
            </div>
            <div className="cc-errors__detail-code">{bar.label}</div>
            <p className="cc-errors__detail-text">{commandCenterErrorsModel.categoryDescription(bar.category)}</p>
            <div className="cc-errors__detail-stats">
                <DetailStat label="Ocurrencias" badge={null} value={withBound(bar.count, lowerBound)} hint={share} />
                <DetailStat label="Dispositivos" badge="Aproximado" value={dailyFormat.count(detail.devices)} hint={`${perDevice} por disp.`} />
                <DetailStat label="Fingerprints" badge={null} value={dailyFormat.count(detail.fingerprints)} hint="variantes" />
            </div>
            <div className="cc-errors__detail-routes">
                <span className="cc-errors__detail-label">Rutas afectadas</span>
                {detail.routes.length === 0 ? <span>{dailyFormat.absent}</span> : detail.routes.map((route) => (
                    <span key={route.route} className="cc-errors__detail-route">
                        <span>{route.route}</span>
                        <b>{dailyFormat.count(route.count)}</b>
                    </span>
                ))}
            </div>
            <dl className="cc-errors__detail-facts">
                <dt>Clase · HTTP</dt>
                <dd>{`${classes} · ${statuses}`}</dd>
                <dt>Mensaje</dt>
                <dd className="cc-errors__detail-message">{detail.message === null ? dailyFormat.absent : detail.message}</dd>
                <dt>Primera vez</dt>
                <dd>{frontendErrorsFormat.nullableDateTime(detail.firstSeenAt)}</dd>
                <dt>Última vez</dt>
                <dd>{detail.lastSeenAt === null ? dailyFormat.absent : frontendErrorsFormat.sinceAsOf(detail.lastSeenAt, asOf)}</dd>
                <dt>Releases</dt>
                <dd>
                    {detail.releases.length === 0 ? dailyFormat.absent : detail.releases.map((release) => (
                        <span key={release} className="cc-errors__release" title={release}>{frontendErrorsFormat.short(release)}</span>
                    ))}
                </dd>
            </dl>
            <div className="cc-errors__detail-cta">Clic para ver sus grupos</div>
        </div>
    );
};

type Hovered = { label: string; position: FloatingPanelPosition };

// The design breaks long codes after each «_» so they wrap under their own column.
const codeLabel = (label: string): string => label.replace(/_/g, "_\u200b");

export const CommandCenterErrorsByTypeCard: React.FC<CardProps & { groups: FrontendErrorsResource<FrontendErrorsGroups>; attention: boolean }> = ({ range, summary, groups, attention }) => {
    const navigate = useNavigate();
    const state = groupsCardState(summary, groups);
    const [hovered, setHovered] = useState<Hovered | null>(null);
    const readyGroups = state.kind === "ready" ? state.data.groups : null;
    const columns = commandCenterErrorsModel.columnsFor(attention);
    // Hovering re-renders the card on every column; the aggregation only reruns when the groups or the width change.
    const bars = useMemo(() => (readyGroups === null ? [] : commandCenterErrorsModel.bars(readyGroups, columns)), [readyGroups, columns]);
    const scale = commandCenterErrorsModel.axis(bars.reduce((top, bar) => Math.max(top, bar.count), 0));
    // Design: the detail opens to the right of a column in the left half, to its left otherwise.
    const show = (event: React.SyntheticEvent<HTMLElement>, label: string, index: number): void => {
        const side = index < bars.length / 2 ? "right" : "left";
        setHovered({ label, position: floatingPanel.place(event.currentTarget.getBoundingClientRect(), DETAIL_WIDTH, DETAIL_HEIGHT, side) });
    };
    const hide = (): void => setHovered(null);
    const lowerBound = state.kind === "ready" && state.lowerBound;
    return (
        <section className="daily-panel cc-errors cc-errors--types" aria-labelledby="cc-errors-types-title">
            <div className="daily-panel__head">
                <div>
                    <h2 id="cc-errors-types-title">Errores del frontend por tipo</h2>
                    <p className="daily-small daily-muted">Últimas 24 h · ocurrencias por código de error, de más a menos · clic en una barra para ver sus grupos</p>
                </div>
                <PageLink range={range} />
            </div>
            <CardBody state={state} label="los errores por tipo">
                {bars.length === 0 ? (
                    <p className="cc-errors__notice">No se han recibido eventos en las últimas 24 h. Sin eventos no es lo mismo que sin errores.</p>
                ) : (
                    <>
                        <ul className="cc-errors__legend" aria-label="Categorías">
                            {frontendErrorsFormat.CATEGORIES.map((category) => (
                                <li key={category} data-category={category}>
                                    <span className="cc-errors__swatch" aria-hidden="true" />
                                    {frontendErrorsFormat.categoryStyle(category).label}
                                </li>
                            ))}
                        </ul>
                        <div className="cc-errors__plot">
                            <div className="cc-errors__grid">
                                <div className="cc-errors__ticks" aria-hidden="true">
                                    {scale.ticks.map((tick) => <span key={tick}>{dailyFormat.count(tick)}</span>)}
                                </div>
                                <ol className="cc-errors__columns" onMouseLeave={hide}>
                                    {bars.map((bar, index) => {
                                        const count = withBound(bar.count, lowerBound);
                                        const category = frontendErrorsFormat.categoryStyle(bar.category).label;
                                        const open = hovered !== null && hovered.label === bar.label;
                                        const height = `${(bar.count / scale.max) * 100}%`;
                                        return (
                                            <li key={bar.label}>
                                                <button
                                                    type="button"
                                                    className="cc-errors__column"
                                                    data-category={bar.category}
                                                    data-open={open}
                                                    aria-label={`${bar.label} · ${category} · ${count} ocurrencias · ver sus grupos`}
                                                    aria-describedby={open ? DETAIL_ID : undefined}
                                                    onClick={() => navigate(commandCenterErrorsModel.href(range, bar.errorCode))}
                                                    onMouseEnter={(event) => show(event, bar.label, index)}
                                                    onFocus={(event) => show(event, bar.label, index)}
                                                    onBlur={hide}
                                                    onKeyDown={(event) => {
                                                        if (event.key === "Escape") hide();
                                                    }}
                                                >
                                                    <span className="cc-errors__column-count" style={{ bottom: height }}>{count}</span>
                                                    <span className="cc-errors__column-bar" aria-hidden="true" style={{ height }} />
                                                </button>
                                                {open && state.kind === "ready" ? (
                                                    <BarDetail bar={bar} lowerBound={lowerBound} asOf={state.data.asOf} position={hovered.position} />
                                                ) : null}
                                            </li>
                                        );
                                    })}
                                </ol>
                            </div>
                            <ol className="cc-errors__codes" aria-hidden="true">
                                {bars.map((bar) => <li key={bar.label}>{codeLabel(bar.label)}</li>)}
                            </ol>
                        </div>
                    </>
                )}
            </CardBody>
        </section>
    );
};

type KpiProps = { label: string; tip?: string; badge: string | null; estimate?: boolean; value: string; hint: string | null };

const Kpi: React.FC<KpiProps> = ({ label, tip, badge, estimate = false, value, hint }) => (
    <div className="cc-errors__kpi" role="group" aria-label={label}>
        <div className="cc-errors__kpi-head">
            <span title={tip}>{label}</span>
            {badge === null ? null : <span className={estimate ? "cc-errors__badge cc-errors__badge--estimate" : "cc-errors__badge"}>{badge}</span>}
        </div>
        <div className="cc-errors__kpi-value">{value}</div>
        {hint === null ? null : <div className="daily-small daily-muted">{hint}</div>}
    </div>
);

const lossBreakdown = (summary: FrontendErrorsSummary): string | null => {
    const loss = summary.clientLoss;
    if (loss === null) return null;
    return [
        `overflow ${dailyFormat.count(loss.overflow)}`,
        `expired ${dailyFormat.count(loss.expired)}`,
        `dropped ${dailyFormat.count(loss.dropped)}`,
    ].join(" · ");
};

const ImpactKpis: React.FC<{ summary: FrontendErrorsSummary; lowerBound: boolean }> = ({ summary, lowerBound }) => {
    const loss = frontendErrorsFormat.lossTotal(summary.clientLoss);
    return (
        <div className="cc-errors__kpis">
            <Kpi label="Errores en el periodo" badge={lowerBound ? null : "Exacto"} value={withBound(summary.total, lowerBound)} hint={null} />
            <Kpi
                label="Dispositivos afectados"
                badge={null}
                value={withBound(summary.affectedDevicesApprox, lowerBound)}
                hint="uno por instalación, no por persona"
            />
            <Kpi
                label="Fingerprints distintos"
                badge={null}
                value={withBound(summary.distinctFingerprints, lowerBound)}
                hint="tipos de fallo diferentes"
            />
            <Kpi
                label="Pérdida del cliente"
                tip={LOSS_TIP}
                badge="Estimación"
                estimate
                value={loss === null ? dailyFormat.absent : `≈ ${dailyFormat.count(loss)}`}
                hint={lossBreakdown(summary)}
            />
        </div>
    );
};

export const CommandCenterErrorsImpactCard: React.FC<CardProps> = ({ range, summary }) => {
    const state = cardState(summary);
    return (
        <section className="daily-panel cc-errors" aria-labelledby="cc-errors-impact-title">
            <div className="daily-panel__head">
                <div>
                    <h2 id="cc-errors-impact-title">Impacto de los errores</h2>
                    <p className="daily-small daily-muted">Últimas 24 h</p>
                </div>
                <PageLink range={range} />
            </div>
            <CardBody state={state} label="el impacto de los errores">
                {state.kind === "ready" ? <ImpactKpis summary={state.data} lowerBound={state.lowerBound} /> : null}
            </CardBody>
        </section>
    );
};
